import React, {createContext, ReactNode, useContext, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {
  useFormik,
  FieldArray,
  FormikProvider,
  FormikProps,
  FieldArrayRenderProps,
} from 'formik';
import moment from 'moment';
import * as actions from '../_redux/actions/tempInspectionsActions';
import {showGlobalMessage} from '../../_common/components/GlobalMessage/actions/showGlobalMessage';
import {messages as M} from '../../../../_metronic/i18n/messages';
import {usePageContext} from "./PageContext";
import {decisionTree} from "../../_common/constants/decisionTree";
import {getFieldID} from "../../_common/utils/getFieldID";

export type Inspection = {
  tempShumaID: number | null;
  shumaReasonID: string;
  propertyTypeID: number | null;
  bankID: number | null;
  bankBranchNumber: string;
  bankBranchName: string;
  bankClerkFullName: string;
  bankEmail: string;
  bankRequestID: string;
  bankBuildType: string;
  shumaTypeID: number | null;
  loanTypeID: number | null;
  loanText: string;
  otherPropertyTypeID: number | null;
  responsibleUser: number | null;
  dueDate: string | Date | null;
  city: number | null;
  street: number | null;
  gush: string;
  helka: string;
  houseNumber: string;
  ttHelka: string;
  apartmentNumber: string;
  notes: string;
  landLotNum: string;
  landLotNumAccordingTaba: string;
  loanSubTypeID: number | null;
  shumaMazminIdentityID: number | null;
  underConstruction: boolean;

  isPriceCatalogUsed?: boolean;
  purchaseCompensation: string;
};

type FormValues = {
  inspections: Inspection[];
};

type FormErrors = Record<string, string>;

type ContextType = {
  formikTempInspections: FormikProps<FormValues>;
  handleCreate: (values: FormValues) => Promise<void>;
  initialForm: Inspection;
  arrayHelpers: FieldArrayRenderProps;
  getInspections: () => Promise<void>;
};

const TempInspectionsListContext = createContext<ContextType | null>(null);

export function useTempInspectionsListContext(): ContextType {
  const ctx = useContext(TempInspectionsListContext);
  if (!ctx) throw new Error('TempInspectionsListContext not found');
  return ctx;
}

export function TempInspectionsListProvider({children}: { children: ReactNode }) {
  const dispatch = useDispatch();
  const {priceQuoteID, setIsTab2Valid} = usePageContext();

  const mapInspection = async (item: any) => {
    return {
      tempShumaID: item.tempShumaID || null,
      shumaReasonID: item.shumaReasonID ? item.shumaReasonID.toString() : '',
      bankBranchNumber: item.bankBranchNumber || '',
      bankBranchName: item.bankBranchName || '',
      bankClerkFullName: item.bankClerkFullName || '',
      bankEmail: item.bankEmail || '',
      bankRequestID: item.bankRequestID || '',
      bankBuildType: item.bankBuildType || '',
      loanText: item.loanText || '',
      gush: item.gush || '',
      helka: item.helka || '',
      houseNumber: item.houseNumber || '',
      ttHelka: item.ttHelka || '',
      apartmentNumber: item.apartmentNumber || '',
      notes: item.notes || '',
      landLotNum: item.landLotNum || '',
      landLotNumAccordingTaba: item.landLotNumAccordingTaba || '',
      underConstruction: Boolean(item.underConstruction),
      responsibleUser: item?.responsibleUser ?? null,
      bankID: item?.bankID ?? null,
      propertyTypeID: item?.propertyTypeID ?? null,
      loanTypeID: item?.loanTypeID ?? null,
      loanSubTypeID: item?.loanSubTypeID ?? null,
      otherPropertyTypeID: item?.otherPropertyTypeID ?? null,
      shumaTypeID: item?.shumaTypeID ?? null,
      shumaMazminIdentityID: item?.shumaMazminIdentityID ?? null,
      city: item?.cityID ?? null,
      street: item?.streetID ?? null,
      dueDate: item?.dueDate ? new Date(item.dueDate) : null,

      isPriceCatalogUsed: Boolean(item?.isPriceCatalogUsed),
      purchaseCompensation: item?.purchaseCompensation ?? '',
    }
  };

  const getInspections = async () => {
    try {
      const result = await actions.getTempInspectionsList(priceQuoteID);
      const mapped = await Promise.all(result.map(mapInspection));

      formikTempInspections.setFieldValue('inspections', mapped);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    getInspections();
  }, []);

  const initialForm: Inspection = {
    tempShumaID: null,
    shumaReasonID: '',
    propertyTypeID: null,
    bankID: null,
    bankBranchNumber: '',
    bankBranchName: '',
    bankClerkFullName: '',
    bankEmail: '',
    bankRequestID: '',
    bankBuildType: '',
    shumaTypeID: null,
    loanTypeID: null,
    loanText: '',
    otherPropertyTypeID: null,
    responsibleUser: null,
    dueDate: null,
    city: null,
    street: null,
    gush: '',
    helka: '',
    houseNumber: '',
    ttHelka: '',
    apartmentNumber: '',
    notes: '',
    landLotNum: '',
    landLotNumAccordingTaba: '',
    loanSubTypeID: null,
    shumaMazminIdentityID: null,
    underConstruction: false,

    isPriceCatalogUsed: false,
    purchaseCompensation: ''
  };

  const validate = (values: FormValues): FormErrors => {
    const {inspections} = values;
    const errors: FormErrors = {};

    inspections.forEach((item, index) => {
      const {
        shumaReasonID,
        propertyTypeID,
        bankID,
        shumaTypeID,
        loanTypeID,
        loanText,
        otherPropertyTypeID,
        responsibleUser,
        shumaMazminIdentityID,
      } = item;

      if (shumaReasonID === decisionTree.rightsEstimation) {
        if (!propertyTypeID) {
          errors[getFieldID('propertyTypeID', index)] = 'Required field';
        }
      } else {
        if (!bankID) {
          errors[getFieldID('bankID', index)] = 'Required field';
        }
        if (!shumaTypeID) {
          errors[getFieldID('shumaTypeID', index)] = 'Required field';
        }
        if (!propertyTypeID) {
          errors[getFieldID('propertyTypeID', index)] = 'Required field';
        }
      }

      if (loanTypeID && `${loanTypeID}` === decisionTree.loanTypeID.other) {
        if (!otherPropertyTypeID) {
          errors[getFieldID('otherPropertyTypeID', index)] = 'Required field';
        }
      }

      if (loanTypeID && `${loanTypeID}` === decisionTree.loanTypeID.another) {
        if (!loanText) {
          errors[getFieldID('loanText', index)] = 'Required field';
        }
      }

      if (!responsibleUser) {
        errors[getFieldID('responsibleUser', index)] = 'Required field';
      }

      if (!shumaMazminIdentityID) {
        errors[getFieldID('shumaMazminIdentityID', index)] = 'Required field';
      }
    });

    return errors;
  };

  const formikTempInspections = useFormik<FormValues>({
    initialValues: {
      inspections: [],
    },
    enableReinitialize: true,
    validate,
    onSubmit: () => {
    },
  });

  useEffect(() => {
    setIsTab2Valid(formikTempInspections.isValid);
  }, [formikTempInspections])

  const buildPayload = (inspection: Omit<Inspection, 'isPriceCatalogUsed'>) => ({
    priceQuoteID,
    tempShumaID: inspection.tempShumaID || -1,
    shumaReasonID: inspection.shumaReasonID,
    bankID: inspection.bankID ?? null,
    propertyTypeID: inspection.propertyTypeID ?? null,
    loanTypeID: inspection.loanTypeID ?? null,
    loanSubTypeID: inspection.loanSubTypeID ?? null,
    shumaTypeID: inspection.shumaTypeID ?? null,
    shumaMazminIdentityID: inspection.shumaMazminIdentityID ?? null,
    responsibleUser: inspection.responsibleUser ?? null,
    otherPropertyTypeID: inspection.otherPropertyTypeID ?? null,
    city: inspection.city ?? null,
    street: inspection.street ?? null,
    bankBranchNumber: inspection.bankBranchNumber,
    bankBranchName: inspection.bankBranchName,
    bankClerkFullName: inspection.bankClerkFullName,
    bankEmail: inspection.bankEmail,
    bankRequestID: inspection.bankRequestID,
    bankBuildType: inspection.bankBuildType,
    loanText: inspection.loanText,
    gush: inspection.gush,
    helka: inspection.helka,
    houseNumber: inspection.houseNumber,
    ttHelka: inspection.ttHelka,
    apartmentNumber: inspection.apartmentNumber,
    notes: inspection.notes,
    landLotNum: inspection.landLotNum,
    landLotNumAccordingTaba: inspection.landLotNumAccordingTaba,
    dueDate: inspection.dueDate
      ? moment(inspection.dueDate).format('YYYY-MM-DD')
      : null,
    underConstruction: inspection.underConstruction,
    purchaseCompensation: inspection.purchaseCompensation,
  });

  const handleCreate = async (values: FormValues) => {
    try {
      await Promise.all(
        values.inspections.map((i) =>
          actions.addEditTempInspection(buildPayload(i))
        )
      );
      await getInspections();
      dispatch(showGlobalMessage('success', M['0']));
    } catch {
      dispatch(showGlobalMessage('error', M['-1']));
    }
  };

  return (
    <FormikProvider value={formikTempInspections}>
      <FieldArray
        name="inspections"
        render={(arrayHelpers) => (
          <TempInspectionsListContext.Provider
            value={{
              formikTempInspections,
              handleCreate,
              initialForm,
              arrayHelpers,
              getInspections,
            }}
          >
            {children}
          </TempInspectionsListContext.Provider>
        )}
      />
    </FormikProvider>
  );
}